<template>
  <CModal :on-update:show="() => emit('update:show', false)" :show="show" :style="{ backgroundColor: themeVars.base }">
    <CCard aria-modal="true" :bordered="false" class="max-w-[640px]" role="dialog" size="medium">
      <div class="text-lg leading-8 tracking-wide">
        {{ t('canvasUI.CBatchMoveItemsModal.moveItems.title') }}
      </div>
      <NBreadcrumb v-if="breadcrumbs.length" class="mt-3 overflow-x-auto" separator=">">
        <NBreadcrumbItem
          v-for="(b, idx) in breadcrumbs"
          :key="b.id ?? 'root'"
          :class="{ 'cursor-pointer': b.id !== currentFolder.id }"
          :clickable="b.id !== currentFolder.id"
          @click="fetchLevel(b.id)"
        >
          {{ !idx ? t('canvasUI.CBatchMoveItemsModal.content.breadcrumbs.root') : b.name }}
        </NBreadcrumbItem>
      </NBreadcrumb>
      <div v-if="isMoving" class="w-full flex items-center justify-center h-[250px] mb-4 mt-3">
        <div class="flex flex-col items-center justify-center">
          <CIcon class="mb-2" :color="themeVars.primary3" fa-type="fas" icon="folder-arrow-up" size="36px" />
          <div class="text-sm" :style="{ color: themeVars.primary3 }">
            {{ t('canvasUI.CBatchMoveItemsModal.moveItems.movingItems') }}
          </div>
        </div>
      </div>
      <RecycleScroller
        v-else-if="folders?.length"
        class="mb-4 mt-3 h-[250px] overflow-y-auto"
        :item-size="50"
        :items="folders"
        key-field="id"
      >
        <template #default="{ item, index }">
          <div
            class="flex justify-between items-center border-b cursor-pointer h-[50px] px-4"
            :style="{ borderTop: index ? `1px solid ${themeVars.primary6}` : undefined }"
            @click="fetchLevel(item.id)"
          >
            <div class="inline-flex items-center truncate">
              <CIcon class="mr-2" icon="folder" size="14px" />
              <span class="truncate">
                {{ parseNameOnly(item.name || '') }}
              </span>
            </div>
            <CIcon icon="angle-right" size="14px" />
          </div>
        </template>
      </RecycleScroller>
      <div v-else class="w-full flex items-center justify-center h-[250px] mb-4 mt-3">
        <NSpin v-if="isFetchingLevel" size="large" />
        <div v-else class="flex flex-col items-center justify-center">
          <CIcon class="mb-2" :color="themeVars.primary3" icon="folder-open" size="36px" />
          <div class="text-sm" :style="{ color: themeVars.primary3 }">
            {{ t('canvasUI.CBatchMoveItemsModal.moveItems.noSubfolders') }}
          </div>
        </div>
      </div>
      <div class="flex justify-between items-center">
        <CButton :disabled="isMoving" @click.stop="emit('update:show', false)">
          {{ t('canvasUI.common.cancel') }}
        </CButton>
        <CButton
          :disabled="currentFolder.id === currentFolderId"
          :loading="isMoving"
          type="primary"
          @click.stop="moveItems"
        >
          {{ t('canvasUI.CBatchMoveItemsModal.moveItems.moveBtn') }}
        </CButton>
      </div>
    </CCard>
  </CModal>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { RecycleScroller } from 'vue-virtual-scroller'
import { useI18n } from 'vue-i18n'
import { NBreadcrumb, NBreadcrumbItem, NSpin } from 'naive-ui'

import CModal from '@lib/components/CModal/CModal.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import type { MiniFolder } from '@openapi'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'

interface Props {
  show: boolean
  currentFolderId: string | null
  selectedItemIds: string[]
  isMoving?: boolean
  isFetchingLevel?: boolean
  folders?: MiniFolder[]
  currentFolder: {
    id: string | null
    name: string
  }
  path: { id: string | null; name: string }[]
}

const props = withDefaults(defineProps<Props>(), {
  isMoving: false,
  isFetchingLevel: false,
  folders: () => [],
})

const emit = defineEmits<{
  'update:show': [show: boolean]
  'fetch-level': [folderId?: string | null]
  'move-items': [targetFolderId: string | null, itemIds: string[]]
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const { parseNameOnly } = useFolderNameDescription()

const breadcrumbs = computed(() => [
  ...props.path.map((folder) => ({
    ...folder,
    name: parseNameOnly(folder.name || ''),
  })),
  {
    id: props.currentFolder.id,
    name: parseNameOnly(props.currentFolder.name || ''),
  },
])

function fetchLevel(folderId?: MiniFolder['id'] | null) {
  emit('fetch-level', folderId)
}

function moveItems() {
  emit('move-items', props.currentFolder.id, props.selectedItemIds)
}

onMounted(() => {
  fetchLevel(props.currentFolder.id)
})
</script>
